/**
 * useUpdateDealCloseDate - Hook para editar a data de conclusão (actual_close_date)
 * de um negócio já ganho ou perdido.
 *
 * @version 1.0.0
 * RELEASE: 2026-05-27 - Versão inicial. Chama RPC `update_deal_actual_close_date`
 *          que valida tenant, status e permissão `crm_param_edit_close_date`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('UpdateDealCloseDate');

interface UpdateDealCloseDateInput {
  dealId: string;
  /** Data/hora em ISO8601 */
  actualCloseDate: string;
}

export function useUpdateDealCloseDate() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dealId, actualCloseDate }: UpdateDealCloseDateInput) => {
      logger.debug('Atualizando data de conclusão', { dealId, actualCloseDate });

      const { error } = await supabase.rpc('update_deal_actual_close_date' as never, {
        p_deal_id: dealId,
        p_actual_close_date: actualCloseDate,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);

      if (error) {
        logger.error('Erro ao atualizar data de conclusão', error);
        throw error;
      }
    },
    onSuccess: (_data, vars) => {
      // Invalida caches relevantes
      queryClient.invalidateQueries({ queryKey: ['crm-deal', vars.dealId] });
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['deal-modal-data', vars.dealId] });
      toast.success('Data de conclusão atualizada');
    },
    onError: (error: any) => {
      const code = error?.code as string | undefined;
      if (code === '42501' || error?.message?.includes('Sem permissão')) {
        toast.error('Você não tem permissão para alterar a data de conclusão');
      } else if (code === '23514' || error?.message?.includes('check_violation') || error?.message?.includes('futura') || error?.message?.includes('ganho ou perdido')) {
        toast.error(error.message || 'Data inválida para conclusão');
      } else {
        toast.error('Não foi possível atualizar a data de conclusão');
      }
    },
  });
}
